import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStable } from "../context/StableContext";
import api from "../lib/api";
import StatusDot from "../components/StatusDot";
import { buildCatMap, sortOverdue } from "../lib/deadlineSort";
import { timeToDue } from "../lib/timeToDue";
import { CheckCircle2, AlertTriangle, Clock, ChevronRight, Loader2, Users, CalendarDays, CalendarClock, ClipboardList, MoreVertical } from "lucide-react";
import { sortByName } from "../lib/people";
import FarrierAppointments from "../components/FarrierAppointments";
import { ActivityStatusSegments } from "../components/AttendanceSegments";
import { ActivityTimeDialog } from "../components/ActivityTimeDialog";
import { ActivityConfirmDialog } from "../components/ActivityConfirmDialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../components/ui/dropdown-menu";
import { toast } from "sonner";

const fmtShort = (d) => (d ? new Date(String(d).slice(0, 10) + "T00:00:00").toLocaleDateString("it-IT", { day: "numeric", month: "long" }) : "—");
// Farrier appointment window (real date / period) — never a countdown or inferred date.
const fmtApptRange = (a) => {
  if (!a?.date_to) return fmtShort(a?.date);
  const f = a.date_from || a.date;
  const df = new Date(f + "T00:00:00"), dt = new Date(a.date_to + "T00:00:00");
  if (df.getMonth() === dt.getMonth() && df.getFullYear() === dt.getFullYear())
    return `${df.getDate()}–${dt.getDate()} ${dt.toLocaleDateString("it-IT", { month: "long" })}`;
  return `${fmtShort(f)} – ${fmtShort(a.date_to)}`;
};
const farrierStatus = (nextDate) => (timeToDue(nextDate).code === "overdue" ? "scaduto" : "upcoming");
const MAX_GROUPS = 5;

function groupByDate(items, getDate) {
  const map = new Map();
  for (const it of items) {
    const key = String(getDate(it) || "").slice(0, 10);
    if (!key) continue;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(it);
  }
  return [...map.entries()].map(([date, list]) => ({ date, items: list }))
    .sort((a, b) => a.date.localeCompare(b.date));
}
const typeSummary = (items, getType) => {
  const counts = {};
  for (const it of items) { const k = getType(it) || "—"; counts[k] = (counts[k] || 0) + 1; }
  return Object.entries(counts).map(([k, v]) => `${v}× ${k}`).join(" · ");
};
// Adaptive view: single record -> horse + single type; one horse many records -> horse + summary;
// many horses -> "N cavalli" + summary. Never shows "1 cavallo" or "1×".
function buildGroupView(g, getHorseId, getName, getType) {
  const horses = new Set(g.items.map(getHorseId));
  if (g.items.length === 1) return { who: getName(g.items[0]), what: getType(g.items[0]) };
  if (horses.size === 1) return { who: getName(g.items[0]), what: typeSummary(g.items, getType) };
  return { who: `${horses.size} cavalli`, what: typeSummary(g.items, getType) };
}

const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
// Operational wall-clock sent to the backend so EXPECTED uses the stable's local time (not the
// server's UTC). MUST be attached to EVERY activity-plan/summary call, including the initial load.
const clientNowIso = () => { const d = new Date(); const p = (n) => String(n).padStart(2, "0"); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:00`; };

// Dashboard-only compact participant rendering: primarily surnames, disambiguated by
// first-name initial on collision, fitting as many as truly fit the available width.
const surnameOf = (full) => { const t = String(full || "").trim().split(/\s+/); return t[0] || String(full || ""); };
const firstInitial = (full) => { const t = String(full || "").trim().split(/\s+/); return t[1] ? t[1][0].toUpperCase() : ""; };
function buildSurnameLabels(names) {
  const counts = {};
  names.forEach((n) => { const s = surnameOf(n); counts[s] = (counts[s] || 0) + 1; });
  return names.map((n) => {
    const s = surnameOf(n);
    if (counts[s] > 1) { const ini = firstInitial(n); return ini ? `${s} ${ini}.` : s; }
    return s;
  });
}

function CompactNames({ names }) {
  const labels = buildSurnameLabels(names);
  const wrapRef = useRef(null);
  const measRef = useRef(null);
  const [visible, setVisible] = useState(labels.length);
  const namesKey = names.join("|");
  useLayoutEffect(() => {
    const compute = () => {
      const wrap = wrapRef.current, meas = measRef.current;
      if (!wrap || !meas) return;
      const avail = wrap.clientWidth;
      const kids = Array.from(meas.children);
      const total = kids.length - 2;
      const sepW = kids[total]?.offsetWidth || 12;      // width of " · "
      const plusW = kids[total + 1]?.offsetWidth || 28; // width of "+9"
      let used = 0, count = 0;
      for (let i = 0; i < total; i++) {
        const w = kids[i]?.offsetWidth || 0;
        const add = count === 0 ? w : sepW + w;
        const remaining = total - (count + 1);
        const reserve = remaining > 0 ? sepW + plusW : 0;
        if (used + add + reserve <= avail) { used += add; count++; } else break;
      }
      setVisible(Math.max(1, count));
    };
    compute();
    const ro = new ResizeObserver(compute);
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [namesKey]);
  const rest = labels.length - visible;
  return (
    <span ref={wrapRef} data-testid="dash-compact-names" className="relative block w-full truncate">
      <span ref={measRef} className="absolute left-0 top-0 invisible whitespace-nowrap" aria-hidden="true">
        {labels.map((l, i) => <span key={i}>{l}</span>)}
        <span> · </span>
        <span>+9</span>
      </span>
      {labels.slice(0, visible).join(" · ")}{rest > 0 ? ` · +${rest}` : ""}
    </span>
  );
}

export default function Dashboard() {
  const { current, role, perms } = useStable();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [cats, setCats] = useState([]);
  const [todayLessons, setTodayLessons] = useState([]);
  const [therDash, setTherDash] = useState(null);
  const [actSummary, setActSummary] = useState(null);
  const [loadErr, setLoadErr] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!current) return;
    setData(null); setTodayLessons([]); setLoadErr(false);
    // Local hardening: a failed/transient Dashboard request must NOT bubble as an
    // uncaught rejection (CRA full-screen overlay). Show an inline retryable state
    // instead. A 404 here is NOT treated as "stable missing" and triggers no redirect.
    api.get(`/stables/${current.id}/dashboard`).then((r) => setData(r.data)).catch(() => setLoadErr(true));
    if (["staff", "manager", "admin"].includes(role)) api.get(`/stables/${current.id}/therapies/dashboard`).then((r) => setTherDash(r.data)).catch(() => {});
    api.get(`/stables/${current.id}/deadline-categories`).then((r) => setCats(r.data)).catch(() => {});
    if (["staff", "manager", "admin"].includes(role)) {
      api.get(`/stables/${current.id}/activity-plan/summary?date=${todayIso()}&now=${clientNowIso()}`).then((r) => setActSummary(r.data)).catch(() => setActSummary(null));
    }
    if (["staff", "manager", "admin"].includes(role)) {
      const t = todayIso();
      api.get(`/stables/${current.id}/calendar?start=${t}&end=${t}`)
        .then((r) => setTodayLessons((r.data || []).filter((o) => !o.is_cancelled))).catch(() => {});
      api.get(`/stables/${current.id}/activities/live`).then((r) => setLiveActs(r.data?.items || [])).catch(() => setLiveActs([]));
    }
  }, [current, role, reloadKey]);

  // Light time-based refresh: presumed-live activities leave "Adesso" at their planned_end even with
  // the Dashboard open (no reload/action). Client-side poll + refetch on tab focus. No websocket/cron.
  useEffect(() => {
    if (!current || !["staff", "manager", "admin"].includes(role)) return;
    const tick = () => { refreshActSummary(); refreshLive(); };
    const iv = setInterval(tick, 20000);
    const onVis = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, role]);

  const canActOps = !perms?.assist;
  const [liveActs, setLiveActs] = useState([]);
  const [timeDlg, setTimeDlg] = useState(null);
  const [confirmDlg, setConfirmDlg] = useState(null);
  const refreshLive = () => api.get(`/stables/${current.id}/activities/live`).then((r) => setLiveActs(r.data?.items || [])).catch(() => setLiveActs([]));
  const refreshActSummary = () => api.get(`/stables/${current.id}/activity-plan/summary?date=${todayIso()}&now=${clientNowIso()}`).then((r) => setActSummary(r.data)).catch(() => {});
  const setActStatus = async (row, status) => {
    try {
      await api.patch(`/stables/${current.id}/activities/${row.id}/status`, { status });
      await refreshActSummary();
    } catch (e) { toast.error("Errore nell'aggiornamento dello stato"); }
  };
  const openStart = (row) => setTimeDlg({ mode: "start", action: "start", item: row, date: row.date || todayIso() });
  const openFinish = (row) => setTimeDlg({ mode: "finish", action: "finish", item: row, date: row.date || todayIso(), minTime: row.actual_start_at });
  const openCorrectStart = (row) => setTimeDlg({ mode: "start", action: "correct_start", item: row, date: row.date || todayIso() });
  const openCorrectEnd = (row) => setTimeDlg({ mode: "finish", action: "correct_end", item: row, date: row.date || todayIso(), minTime: row.actual_start_at });
  const doStart = async (row, iso) => {
    try { await api.post(`/stables/${current.id}/activities/${row.id}/start`, { actual_start_at: iso }); await Promise.all([refreshLive(), refreshActSummary()]); }
    catch (e) { toast.error("Errore nell'avvio"); }
  };
  const doFinish = async (row, iso) => {
    try { await api.post(`/stables/${current.id}/activities/${row.id}/finish`, { actual_end_at: iso }); await Promise.all([refreshLive(), refreshActSummary()]); }
    catch (e) { toast.error("Errore nel completamento"); }
  };
  const doConfirm = async (row, patch) => {
    try { await api.post(`/stables/${current.id}/activities/${row.id}/confirm`, patch || {}); await Promise.all([refreshLive(), refreshActSummary()]); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore nella conferma"); }
  };
  const doCorrectTimes = async (row, patch) => {
    try { await api.patch(`/stables/${current.id}/activities/${row.id}/times`, patch); await Promise.all([refreshLive(), refreshActSummary()]); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore nella rettifica"); }
  };
  const declineStart = async (row) => {
    try { await api.post(`/stables/${current.id}/activities/${row.id}/decline-start`); await refreshActSummary(); }
    catch (e) { toast.error("Errore nell'aggiornamento"); }
  };
  const skipAct = async (row) => {
    try { await api.patch(`/stables/${current.id}/activities/${row.id}/status`, { status: "skipped" }); await Promise.all([refreshLive(), refreshActSummary()]); }
    catch (e) { toast.error("Errore nell'aggiornamento"); }
  };

  if (loadErr && !data)
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center px-6" data-testid="dashboard-load-error">
        <p className="text-[#1B3B2B] font-semibold">Impossibile caricare i dati della Dashboard. Riprova.</p>
        <button onClick={() => setReloadKey((k) => k + 1)} data-testid="dashboard-retry-btn"
          className="mt-4 inline-flex items-center justify-center rounded-md h-10 px-5 text-sm font-medium text-[#FAF8F5] bg-[#1B3B2B] hover:bg-[#142E1F] transition-colors">Riprova</button>
      </div>
    );

  if (!data)
    return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#1B3B2B]" /></div>;

  const catMap = buildCatMap(cats);
  const dOverdue = sortOverdue(data.overdue, catMap);
  // Dashboard radar: overdue shown without limit; FUTURE deadlines only within the next 45 days.
  const upcomingRaw = data.upcoming || data.next30 || [];
  const d45 = new Date(); d45.setHours(0, 0, 0, 0); d45.setDate(d45.getDate() + 45);
  const dUpcoming = upcomingRaw.filter((d) => { const due = new Date(String(d.due_date).slice(0, 10) + "T00:00:00"); return due <= d45; });
  const fAll = data.upcoming_farrier || [];
  const fOverdue = fAll.filter((f) => farrierStatus(f.next_date) === "scaduto");
  const fUpcoming = fAll.filter((f) => farrierStatus(f.next_date) !== "scaduto");

  const gOverdueD = groupByDate(dOverdue, (d) => d.due_date);
  const gUpcomingD = groupByDate(dUpcoming, (d) => d.due_date);
  const gOverdueF = groupByDate(fOverdue, (f) => f.next_date);
  const gUpcomingF = groupByDate(fUpcoming, (f) => f.next_date);
  // "In programma" timeline: interleave real farrier appointments (with maniscalco name)
  // chronologically among the per-horse shoeing due-dates. Reuses existing planning, no 2nd system.
  const apptUpcoming = data.farrier_appointments || [];
  const farrierTimeline = [
    ...gUpcomingF.map((g) => ({ kind: "shoeing", date: g.date, g })),
    ...apptUpcoming.map((a) => ({ kind: "appt", date: a.date_from || a.date, a })),
  ].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : (a.kind === "appt" ? -1 : 1)));

  const dMissing = data.missing || [];
  const hasMissing = dMissing.length > 0;
  const therHas = !!(therDash && (therDash.overdue_count > 0 || therDash.today_count > 0));
  const scadHas = dOverdue.length > 0 || dUpcoming.length > 0;
  const mascHas = fOverdue.length > 0 || fUpcoming.length > 0 || !!data.next_farrier_appointment;
  const moduleCount = (therHas ? 1 : 0) + (scadHas ? 1 : 0) + (mascHas ? 1 : 0);
  const moduleCols = moduleCount >= 3 ? "lg:grid-cols-3" : moduleCount === 2 ? "lg:grid-cols-2" : "lg:grid-cols-1";
  // "Lezioni di oggi": only future / currently-in-progress lessons (ended ones drop off).
  const nowHM = (() => { const d = new Date(); return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; })();
  const liveLessons = (todayLessons || []).filter((o) => (o.end_time || "") > nowHM || o.attendance_status === "none" || o.attendance_status === "partial");

  // Compact KPI widgets (modular: extend this array for future configurable widgets).
  const kpis = [
    { key: "present", label: "Cavalli presenti", value: data.present_count, icon: CheckCircle2, color: "#1B5E20", bg: "#E8F5E9", go: () => navigate("/cavalli?presence=presente") },
    { key: "absent", label: "Temporaneamente assenti", value: data.absent_count, icon: Clock, color: "#E65100", bg: "#FFF3E0", go: () => navigate("/cavalli?presence=assente_temporaneo") },
  ];

  const deadlineGroup = (g) => {
    const v = buildGroupView(g, (d) => d.horse_id, (d) => d.horse_name, (d) => d.category);
    const single = g.items.length === 1;
    const aggregate = new Set(g.items.map((d) => d.horse_id)).size > 1;
    return <GroupRow key={g.date} testid={`dash-deadline-group-${g.date}`} date={g.date} who={v.who} what={v.what} aggregate={aggregate}
      onClick={() => navigate(single ? `/scadenze?focus=${g.items[0].id}` : `/scadenze?date=${g.date}`)} />;
  };
  const farrierGroup = (g) => {
    const v = buildGroupView(g, (f) => f.horse_id, (f) => f.horse_name, (f) => f.work_type);
    const single = g.items.length === 1;
    const aggregate = new Set(g.items.map((f) => f.horse_id)).size > 1;
    return <GroupRow key={g.date} testid={`dash-farrier-group-${g.date}`} date={g.date} who={v.who} what={v.what} aggregate={aggregate}
      onClick={() => navigate(single ? `/mascalcia?focus=${g.items[0].horse_id}` : `/mascalcia?date=${g.date}`)} />;
  };
// Real appointment strip inside the "In programma" timeline — MILESTONE (golden line, no box),
// integrating date/period + maniscalco name. Clearly distinct from operative records.
  const apptStrip = (a) => (
    <button key={`appt-${a.id}`} data-testid={`dash-farrier-appt-${a.id}`} onClick={() => navigate("/mascalcia")}
      className="w-full flex items-center gap-2 px-3 py-2.5 group">
      <span className="h-px flex-1" style={{ background: "linear-gradient(to right, transparent, #CDBE9E)" }} />
      <span className="text-[11px] font-medium tracking-wide whitespace-nowrap flex items-center gap-1 text-[#8C532B] group-hover:text-[#5A3419] transition-colors">
        <CalendarClock className="h-3 w-3" />{fmtApptRange(a)}{a.time ? ` · ${a.time}` : ""} · {a.farrier}
      </span>
      <span className="h-px flex-1" style={{ background: "linear-gradient(to left, transparent, #CDBE9E)" }} />
    </button>
  );

  return (
    <div className="space-y-5 lg:space-y-7 eq-fade-up">
      {/* Compact KPI mini-cards (desktop + mobile) */}
      <div className="grid grid-cols-2 gap-3 lg:max-w-md">
        {kpis.map((s) => (
          <button key={s.key} data-testid={`stat-${s.key}`} onClick={s.go}
            className="flex items-center gap-3 bg-white rounded-xl border border-[#E5DEC9] px-3 py-2.5 hover:border-[#8C532B] transition-colors">
            <div className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: s.bg }}>
              <s.icon className="h-4.5 w-4.5" style={{ color: s.color }} />
            </div>
            <div className="min-w-0 text-left">
              <p className="font-display text-xl font-bold text-[#1B3B2B] leading-none">{s.value}</p>
              <p className="text-[11px] text-[#5F6B64] truncate mt-1">{s.label}</p>
            </div>
          </button>
        ))}
      </div>

      {(() => {
        const opItems = [
          ...liveActs.map((a) => ({ ...a, _op: "live" })),
          ...((actSummary?.presumed_live) || []).map((a) => ({ ...a, _op: "presumed" })),
        ];
        if (opItems.length === 0) return null;
        return (
        <section data-testid="dash-live-activities">
          <div className="lg:max-w-md">
            <div className="flex items-center gap-1.5 mb-2 px-1">
              <Clock className="h-3.5 w-3.5 text-[#1B5E20]" />
              <h2 className="font-display font-bold text-[#1B3B2B] text-sm">Attività adesso</h2>
            </div>
            <div className="bg-white rounded-xl border border-[#E5DEC9] divide-y divide-[#F0EAE1] overflow-hidden">
              {opItems.map((a) => {
                if (a._op === "presumed") {
                  const startHM = a.presumed_start_at ? String(a.presumed_start_at).slice(11, 16) : (a.start_at ? String(a.start_at).slice(11, 16) : (a.time || ""));
                  const endHM = a.expected_end_at ? String(a.expected_end_at).slice(11, 16) : "";
                  return (
                    <div key={`pl-${a.id}`} data-testid={`dash-presumed-row-${a.id}`} className="flex items-center gap-2 px-3 py-2.5 text-sm">
                      <span className="eq-live-dot" aria-hidden="true" />
                      <button type="button" data-testid={`dash-presumed-open-${a.id}`} onClick={() => navigate(`/attivita?edit=${a.id}&editdate=${a.date}`)} className="min-w-0 flex-1 text-left hover:opacity-80">
                        <span className="block text-[#1B3B2B] leading-snug"><span className="font-semibold">{a.horse_name}</span> — {a.activity_type_label}</span>
                        {a.assignee_name && <span className="block text-[#5F6B64] leading-snug break-words" data-testid={`dash-presumed-assignee-${a.id}`}>{a.assignee_name}</span>}
                        <span className="block text-[11px] text-[#1B5E20] font-medium" data-testid={`dash-presumed-info-${a.id}`}>{startHM || "—"}{endHM ? `–${endHM}` : ""}</span>
                      </button>
                      {canActOps && <button data-testid={`dash-presumed-finish-${a.id}`} onClick={(e) => { e.stopPropagation(); openFinish(a); }} className="h-7 px-2.5 rounded-md text-xs font-medium border border-[#1B5E20] text-[#1B5E20] hover:bg-[#E8F5E9] shrink-0">Termina</button>}
                      {canActOps && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button data-testid={`dash-presumed-menu-${a.id}`} className="h-7 w-7 rounded-md text-[#8C6D4F] hover:bg-[#F0EAE1] flex items-center justify-center shrink-0"><MoreVertical className="h-4 w-4" /></button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem data-testid={`dash-presumed-correct-${a.id}`} onClick={() => openStart(a)}>Correggi inizio</DropdownMenuItem>
                            <DropdownMenuItem data-testid={`dash-presumed-decline-${a.id}`} onClick={() => declineStart(a)}>Non iniziata</DropdownMenuItem>
                            <DropdownMenuItem data-testid={`dash-presumed-skip-${a.id}`} onClick={() => skipAct(a)} className="text-red-600">Non effettuata</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  );
                }
                const startHM = a.actual_start_at ? String(a.actual_start_at).slice(11, 16) : (a.start_at ? String(a.start_at).slice(11, 16) : (a.time || ""));
                let endHM = a.expected_end_at ? String(a.expected_end_at).slice(11, 16) : "";
                if (a.actual_start_at && a.start_at && a.expected_end_at) {
                  const dur = new Date(a.expected_end_at) - new Date(a.start_at);
                  if (dur > 0) { const de = new Date(new Date(a.actual_start_at).getTime() + dur); endHM = `${String(de.getHours()).padStart(2, "0")}:${String(de.getMinutes()).padStart(2, "0")}`; }
                }
                return (
                  <div key={a.id} data-testid={`dash-live-row-${a.id}`} className="flex items-center gap-3 px-3 py-2.5 text-sm">
                    <span className="eq-live-dot" aria-hidden="true" />
                    <button type="button" data-testid={`dash-live-open-${a.id}`} onClick={() => navigate(`/attivita?edit=${a.id}&editdate=${a.date}`)} className="min-w-0 flex-1 text-left hover:opacity-80">
                      <span className="block text-[#1B3B2B] leading-snug"><span className="font-semibold">{a.horse_name}</span> — {a.activity_type_label}</span>
                      {a.assignee_name && <span className="block text-[#5F6B64] leading-snug break-words" data-testid={`dash-live-assignee-${a.id}`}>{a.assignee_name}</span>}
                      <span className="block text-[11px] text-[#1B5E20] font-medium" data-testid={`dash-live-info-${a.id}`}>{startHM || "—"}{endHM ? `–${endHM}` : ""}</span>
                    </button>
                    {canActOps && <button data-testid={`dash-live-finish-${a.id}`} onClick={(e) => { e.stopPropagation(); openFinish(a); }} className="h-7 px-2.5 rounded-md text-xs font-medium border border-[#1B5E20] text-[#1B5E20] hover:bg-[#E8F5E9] shrink-0">Termina</button>}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
        );
      })()}

      {actSummary && (actSummary.to_confirm || []).length > 0 && (
        <section data-testid="dash-to-confirm">
          <div className="lg:max-w-md">
            <div className="flex items-center gap-1.5 mb-2 px-1">
              <CheckCircle2 className="h-3.5 w-3.5 text-[#8C532B]" />
              <h2 className="font-display font-bold text-[#1B3B2B] text-sm">Attività da confermare</h2>
            </div>
            <div className="bg-white rounded-xl border border-[#E5DEC9] divide-y divide-[#F0EAE1] overflow-hidden">
              {(actSummary.to_confirm || []).map((a) => {
                const sHM = a.actual_start_at ? String(a.actual_start_at).slice(11, 16) : "";
                const eHM = a.actual_end_at ? String(a.actual_end_at).slice(11, 16) : "";
                const pS = a.start_at ? String(a.start_at).slice(11, 16) : (a.time || "");
                const pE = a.expected_end_at ? String(a.expected_end_at).slice(11, 16) : "";
                return (
                  <div key={`tc-${a.id}`} data-testid={`dash-toconfirm-row-${a.id}`} className="flex items-center gap-2 px-3 py-2.5 text-sm">
                    <button type="button" data-testid={`dash-toconfirm-open-${a.id}`} onClick={() => navigate(`/attivita?edit=${a.id}&editdate=${a.date}`)} className="min-w-0 flex-1 text-left hover:opacity-80">
                      <span className="block text-[#1B3B2B] leading-snug"><span className="font-semibold">{a.horse_name}</span> — {a.activity_type_label}</span>
                      {a.assignee_name && <span className="block text-[#5F6B64] leading-snug break-words" data-testid={`dash-toconfirm-assignee-${a.id}`}>{a.assignee_name}</span>}
                      <span className="block text-[11px] text-[#8C532B] font-medium" data-testid={`dash-toconfirm-info-${a.id}`}>Programmata {pS || "—"}{pE ? `–${pE}` : ""}</span>
                    </button>
                    {canActOps && <button data-testid={`dash-toconfirm-confirm-${a.id}`} onClick={(e) => { e.stopPropagation(); setConfirmDlg(a); }} className="h-7 px-2.5 rounded-md text-xs font-medium border border-[#8C532B] text-[#8C532B] hover:bg-[#F7EFE0] shrink-0">Da confermare</button>}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {actSummary && actSummary.rows.length > 0 && (
        <section data-testid="dash-activities-today">
          <div className="lg:max-w-md">
            <button onClick={() => navigate("/attivita")} data-testid="dash-activities-title" className="flex items-center gap-1.5 mb-2 px-1 group">
              <ClipboardList className="h-3.5 w-3.5 text-[#8C532B]" />
              <h2 className="font-display font-bold text-[#1B3B2B] text-sm group-hover:text-[#8C532B]">Attività cavalli · Oggi</h2>
              <ChevronRight className="h-3.5 w-3.5 text-[#C9BDA0] group-hover:text-[#8C532B]" />
            </button>
            <div className="bg-white rounded-xl border border-[#E5DEC9] overflow-hidden">
              <p className="px-3 py-2 text-xs text-[#5F6B64] border-b border-[#F0EAE1]" data-testid="dash-activities-counts">{(() => {
                const c = actSummary.counts || {}; const parts = [];
                if (c.todo) parts.push(`${c.todo} da fare`);
                if (c.to_confirm) parts.push(`${c.to_confirm} da confermare`);
                if (c.done) parts.push(`${c.done} ${c.done === 1 ? "completata" : "completate"}`);
                return parts.join(" · ") || "Nessuna attività";
              })()}</p>
              {actSummary.rows.filter((r) => r.status !== "done").map((r) => (
                <div key={r.id} data-testid={`dash-act-row-${r.id}`} className={`flex items-center gap-3 px-3 py-2 text-sm border-b border-[#F0EAE1] last:border-0 ${r.status !== "todo" ? "opacity-60" : ""}`}>
                  <span className="w-11 text-xs font-mono-label text-[#8C532B] shrink-0">{r.time || "—"}</span>
                  <span className="min-w-0 flex-1 truncate text-[#1B3B2B]"><span className="font-semibold">{r.horse_name}</span> — {r.activity_type_label}{r.assignee_name ? <span className="text-[#5F6B64]"> · {r.assignee_name}</span> : ""}</span>
                  {r.to_verify && <span data-testid={`dash-act-toverify-${r.id}`} className="text-[10px] font-semibold text-[#8C532B] bg-[#F7EFE0] rounded-full px-1.5 py-0.5 shrink-0">Da verificare</span>}
                  {!r.actual_start_at && !r.actual_end_at
                    ? <button data-testid={`dash-act-start-${r.id}`} disabled={!canActOps} onClick={() => openStart(r)} className="h-7 px-2.5 rounded-md text-xs font-medium border border-[#1B5E20] text-[#1B5E20] hover:bg-[#E8F5E9] shrink-0 disabled:opacity-50">Avvia</button>
                    : <ActivityStatusSegments status={r.status} disabled={!canActOps} onSelect={(s) => setActStatus(r, s)} testId={`dash-act-status-${r.id}`} size="sm" />}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {liveLessons.length > 0 && (
        <section data-testid="dash-today-lessons">
          <div className="lg:max-w-md">
            <button onClick={() => navigate("/calendario")} data-testid="dash-today-title" className="flex items-center gap-1.5 mb-2 px-1 group">
              <CalendarDays className="h-3.5 w-3.5 text-[#8C532B]" />
              <h2 className="font-display font-bold text-[#1B3B2B] text-sm group-hover:text-[#8C532B]">Lezioni di oggi</h2>
              <ChevronRight className="h-3.5 w-3.5 text-[#C9BDA0] group-hover:text-[#8C532B]" />
            </button>
            <div className="bg-white rounded-xl border border-[#E5DEC9] divide-y divide-[#F0EAE1] overflow-hidden">
              {liveLessons.map((o) => {
                const names = sortByName((o.participants || []).filter((p) => p.att_status !== "absent"), (p) => p.name || "").map((p) => p.name).filter(Boolean);
                return (
                  <button key={o.id} data-testid={`dash-lesson-${o.id}`} onClick={() => navigate(`/calendario?att=${encodeURIComponent(o.id)}`)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[#FCFAF6] transition-colors">
                    <span className="h-8 w-1.5 rounded-full shrink-0" style={{ backgroundColor: o.color || "#1B3B2B" }} />
                    <span className="font-semibold text-sm text-[#1B3B2B] tabular-nums shrink-0">{o.start_time}</span>
                    <span className="text-sm text-[#41504A] flex-1 min-w-0">
                      {(o.title || "").trim()
                        ? <span data-testid={`dash-lesson-title-${o.id}`} className="font-medium text-[#1B3B2B] truncate block">{o.title.trim()}</span>
                        : names.length === 0 ? <span className="text-[#8C6D4F] italic">Nessun partecipante previsto</span> : <CompactNames names={names} />}
                    </span>
                    {o.has_prova && <span className="text-[9px] font-bold text-[#7C3AED] shrink-0">PROVA</span>}
                    {(o.end_time || "") <= nowHM && (o.attendance_status === "none" || o.attendance_status === "partial") &&
                      <span data-testid={`dash-lesson-todo-${o.id}`} className="text-[9px] font-semibold text-[#8C532B] bg-[#F7EFE0] rounded-full px-1.5 py-0.5 shrink-0">Presenze da registrare</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {(scadHas || mascHas || therHas) ? (
        <div data-testid="dash-modules" className={`grid gap-4 lg:gap-6 ${moduleCols}`}>
          {therHas && (
            <Module title="Somministrazioni" testid="dash-module-therapies">
              {therDash.overdue_count > 0 && (
                <Group title="In ritardo" tone="danger" testid="dash-therapies-overdue"
                  count={therDash.overdue_count} countLabel="in ritardo" onAll={() => navigate("/terapie")}>
                  {therDash.overdue.slice(0, MAX_GROUPS).map((a) => (
                    <button key={a.id} data-testid={`dash-ther-${a.id}`} onClick={() => navigate("/terapie")}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[#FCFAF6] transition-colors">
                      <StatusDot status="scaduto" />
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-[#1B3B2B] text-sm truncate">{a.horse_name} · {a.treatment_name}{a.dose ? ` · ${a.dose}` : ""}</p>
                        <p className="text-xs text-[#B91C1C] font-semibold">Previsto {a.scheduled_time} · in ritardo</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-[#C9BDA0] shrink-0" />
                    </button>
                  ))}
                </Group>
              )}
              {therDash.today_count > 0 && (
                <Group title="Da fare oggi" tone="warn" testid="dash-therapies-today"
                  count={therDash.today_count} countLabel="da fare" onAll={() => navigate("/terapie")}>
                  {therDash.today.slice(0, MAX_GROUPS).map((a) => (
                    <button key={a.id} data-testid={`dash-ther-today-${a.id}`} onClick={() => navigate("/terapie")}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[#FCFAF6] transition-colors">
                      <StatusDot status="in_scadenza" />
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-[#1B3B2B] text-sm truncate">{a.horse_name} · {a.treatment_name}{a.dose ? ` · ${a.dose}` : ""}</p>
                        <p className="text-xs text-[#8C6D4F]">Previsto oggi alle {a.scheduled_time}</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-[#C9BDA0] shrink-0" />
                    </button>
                  ))}
                </Group>
              )}
            </Module>
          )}
          {scadHas && (
            <Module title="Scadenzario" testid="dash-module-deadlines">
              {dOverdue.length > 0 && (
                <Group title="Scaduti" tone="danger" testid="dash-deadlines-overdue"
                  count={dOverdue.length} countLabel="da gestire" onAll={() => navigate("/scadenze?group=overdue")}>
                  {gOverdueD.slice(0, MAX_GROUPS).map(deadlineGroup)}
                </Group>
              )}
              {dUpcoming.length > 0 && (
                <Group title="In scadenza" tone="warn" testid="dash-deadlines-next"
                  count={dUpcoming.length} countLabel="in arrivo" onAll={() => navigate("/scadenze?group=upcoming")}>
                  {gUpcomingD.slice(0, MAX_GROUPS).map(deadlineGroup)}
                </Group>
              )}
            </Module>
          )}
          {mascHas && (
            <Module title="Mascalcia" testid="dash-module-farrier">
              <FarrierAppointments stableId={current.id} canOps={perms?.canOps} compact />
              {fOverdue.length > 0 && (
                <Group title="Scaduti" tone="danger" testid="dash-farrier-overdue"
                  count={fOverdue.length} countLabel="da gestire" onAll={() => navigate("/mascalcia?group=overdue")}>
                  {gOverdueF.slice(0, MAX_GROUPS).map(farrierGroup)}
                </Group>
              )}
              {(fUpcoming.length > 0 || apptUpcoming.length > 0) && (
                <Group title="In programma" tone="warn" testid="dash-farrier-next"
                  count={fUpcoming.length} countLabel="in arrivo" onAll={() => navigate("/mascalcia?group=upcoming")}>
                  {farrierTimeline.slice(0, MAX_GROUPS).map((it) => it.kind === "appt" ? apptStrip(it.a) : farrierGroup(it.g))}
                </Group>
              )}
            </Module>
          )}
        </div>
      ) : !hasMissing ? (
        <div data-testid="dash-all-clear" className="bg-white rounded-xl border border-[#E5DEC9] px-4 py-10 text-center">
          <CheckCircle2 className="h-8 w-8 text-[#2E7D32] mx-auto mb-2" />
          <p className="font-display font-semibold text-[#1B3B2B]">Tutto in ordine</p>
          <p className="text-sm text-[#5F6B64] mt-1">Nessuna scadenza o intervento da gestire al momento.</p>
        </div>
      ) : null}

      {hasMissing && (
        <section data-testid="dash-missing-block">
          <div className="lg:max-w-md">
            <div className="flex items-center justify-between gap-2 mb-1.5 px-1">
              <h2 className="font-display font-semibold text-[#8C6D4F] text-sm">Da regolarizzare</h2>
              <button data-testid="dash-missing-all" onClick={() => navigate("/scadenze?group=missing")}
                className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 bg-[#F0EAE1] text-[#8C6D4F] hover:brightness-95 transition">
                {dMissing.length} <ChevronRight className="h-3 w-3" />
              </button>
            </div>
            <div className="rounded-xl border border-[#E5DEC9] divide-y divide-[#F0EAE1] overflow-hidden bg-white">
              {dMissing.slice(0, MAX_GROUPS).map((d) => (
                <button key={d.id} data-testid={`dash-missing-${d.id}`} onClick={() => navigate(`/scadenze?focus=${d.id}`)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[#FCFAF6] transition-colors">
                  <span className="inline-block h-2.5 w-2.5 rounded-full shrink-0 bg-[#C9BDA0]" />
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-[#1B3B2B] text-sm truncate">{d.horse_name}</p>
                    <p className="text-xs text-[#41504A] truncate">{d.category}</p>
                  </div>
                  <span className="text-[11px] font-semibold text-[#A03D62] shrink-0">Mancante</span>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {timeDlg && (
        <ActivityTimeDialog open mode={timeDlg.mode} date={timeDlg.date} minTime={timeDlg.minTime}
          onClose={() => setTimeDlg(null)}
          onConfirm={(iso) => {
            const t = timeDlg; setTimeDlg(null);
            if (t.action === "start") doStart(t.item, iso);
            else if (t.action === "finish") doFinish(t.item, iso);
            else if (t.action === "correct_start") doCorrectTimes(t.item, { actual_start_at: iso });
            else if (t.action === "correct_end") doCorrectTimes(t.item, { actual_end_at: iso });
          }} />
      )}
      {confirmDlg && (
        <ActivityConfirmDialog open item={confirmDlg}
          onClose={() => setConfirmDlg(null)}
          onConfirm={(patch) => { const it = confirmDlg; setConfirmDlg(null); doConfirm(it, patch); }}
          onSkip={() => { const it = confirmDlg; setConfirmDlg(null); skipAct(it); }} />
      )}
    </div>
  );
}

const TONE = {
  danger: { chip: "bg-[#FEE2E2] text-[#B91C1C]" },
  warn: { chip: "bg-[#FFF8E1] text-[#B26A00]" },
};

// Top-level Dashboard column = one operational MODULE (Somministrazioni / Scadenzario / Mascalcia).
// Its inner groups stack vertically; NO max-width so the module fills its column.
function Module({ title, testid, children }) {
  return (
    <section data-testid={testid} className="space-y-3">
      <h2 className="font-display font-bold text-[#1B3B2B] text-sm px-1">{title}</h2>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Group({ title, tone, count, countLabel, onAll, testid, children }) {
  const t = TONE[tone] || TONE.warn;
  return (
    <section data-testid={testid}>
      <div className="flex items-center justify-between gap-2 mb-1.5 px-1">
        <h3 className="font-display font-semibold text-[#1B3B2B] text-sm truncate min-w-0">{title}</h3>
        <button data-testid={`${testid}-all`} onClick={onAll}
          className={`shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 ${t.chip} hover:brightness-95 transition`}>
          {count} {countLabel} <ChevronRight className="h-3 w-3" />
        </button>
      </div>
      <div className={`rounded-xl border divide-y overflow-hidden bg-white ${tone === "danger" ? "border-[#FCA5A5] divide-[#FCDADA]" : "border-[#E5DEC9] divide-[#F0EAE1]"}`}>
        {children}
      </div>
    </section>
  );
}

// Operative block: CHI -> COSA -> QUANDO (data + time-to-due).
// Aggregate rows ("N cavalli") get a discreet neutral icon + faint tint — never a temporal colour.
function GroupRow({ who, what, date, onClick, testid, aggregate }) {
  const tt = timeToDue(date);
  return (
    <button data-testid={testid} onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-[#FCFAF6] active:bg-[#FCFAF6] ${aggregate ? "bg-[#FBF8F2]" : ""}`}>
      <StatusDot status={tt.dot} />
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-[#1B3B2B] text-sm truncate flex items-center gap-1.5">
          {aggregate && <Users className="h-3.5 w-3.5 text-[#8C6D4F] shrink-0" strokeWidth={2.2} />}
          <span className="truncate">{who}</span>
        </p>
        <p className="text-xs text-[#41504A] truncate">{what}</p>
        <p className="text-xs font-medium mt-0.5 flex flex-wrap gap-x-1.5">
          <span className="text-[#5F6B64]">{fmtShort(date)}</span>
          <span style={{ color: tt.color }}>· {tt.label}</span>
        </p>
      </div>
      <ChevronRight className="h-4 w-4 text-[#C9BDA0] shrink-0" />
    </button>
  );
}
